// ────────────────────────────────────────────────────────────────────────────
// The builder shell: band list · inspector · live preview.
//
// One draft layout lives here and everything else is a pure view of it. Bands
// are edited in place, the preview re-renders off the same draft, and Save
// writes the whole layout in one go — so there is no half-saved template and
// no diffing to reason about.
//
// Structural bands (header, lines, totals, …) can be *disabled* but not
// deleted: a template that is missing its line table is not a design choice,
// it is a broken document. Text blocks are the exception — those are additive
// by nature, so they can be added and removed freely.
// ────────────────────────────────────────────────────────────────────────────
import { useAuth0 } from '@auth0/auth0-react';
import { Button, ConfirmDialog, cn, showToast } from '@my/ui';
import { ArrowLeft, Copy, FileText, Plus, Star, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useOrganisation } from '@my/directory-api/hooks';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { parseOrderReferenceLabels } from '@/utils/orderReferenceLabels';
import { BandInspector } from './BandInspector';
import { PdfPreviewPane } from './PdfPreviewPane';
import { PageStyleInspector } from './PageStyleInspector';
import { moveItem } from '../lib/arrays';
import { dedupeLayout } from '../lib/migrateLayout';
import { appendBand } from '../lib/bands';
import { useDocumentTemplates } from '../lib/useDocumentTemplates';
import { useOrgPreviewData } from '../lib/useOrgPreviewData';
import {
  BAND_KIND_LABEL,
  DOC_TYPE_LABEL,
  type Band,
  type DocumentLayout,
} from '../lib/types';

/**
 * True when a band sits above the line table, which is the region laid out as a
 * grid — the only place a band's column means anything.
 */
function isInHeaderGrid(layout: DocumentLayout, bandId: string): boolean {
  const linesIndex = layout.bands.findIndex((b) => b.kind === 'lines');
  if (linesIndex === -1) return false;
  return layout.bands.findIndex((b) => b.id === bandId) < linesIndex;
}

/** Kinds the operator may remove outright rather than just switch off. */
const DELETABLE_KINDS: Band['kind'][] = ['text'];

export function DocumentBuilder() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth0();
  const organisationId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const { templates, isLoading, isSaving, saveTemplate, setDefault, duplicateTemplate, refresh } =
    useDocumentTemplates(organisationId);

  const template = templates.find((t) => t.id === templateId);

  const [draft, setDraftState] = useState<DocumentLayout | null>(null);

  /**
   * Every write to the draft goes through the deduplicator.
   *
   * A layout carrying the same band twice is not merely untidy, it is
   * unreadable: both lists here are keyed by identity (`band.id`, `col.key`),
   * so React collapses the repeat into ONE row while the PDF renderer draws
   * both — the operator sees a band listed once and printed twice, and moving
   * it by index appears to move the wrong one. Guarding the setter rather than
   * only the loader means a draft cannot be left holding duplicates by any
   * route: a layout stored damaged, a hot reload that kept older state, or a
   * future editing path that has not thought about it.
   */
  const setDraft = (next: DocumentLayout) => setDraftState(dedupeLayout(next));
  // The name is edited locally and written on Save. Writing per keystroke was
  // fine against localStorage; against the API it would be a PUT per character.
  const [name, setName] = useState('');
  const [selectedBandId, setSelectedBandId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Reload the draft whenever the edited template changes identity (route hop,
  // or a Save that stamped a new `updatedUtc`).
  useEffect(() => {
    if (!template) return;
    setDraft(JSON.parse(JSON.stringify(template.layout)) as DocumentLayout);
    setName(template.name);
    setSelectedBandId(template.layout.bands[0]?.id ?? null);
  }, [template?.id, template?.updatedUtc]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data: previewData,
    logoStatus,
    logoError,
    bank,
  } = useOrgPreviewData(organisationId, template?.docType ?? 'Invoice');

  // The org names its four order-reference slots; the Order ref column offers
  // those names rather than "Reference 2".
  const orgQuery = useOrganisation(organisationId);
  const referenceLabels = useMemo(
    () => parseOrderReferenceLabels(orgQuery.data?.tmsOrderReferenceLabelsJson),
    [orgQuery.data?.tmsOrderReferenceLabelsJson],
  );

  if (!template) {
    // Distinguish "still loading" from "gone" — the list arrives asynchronously
    // now, and telling someone their layout was deleted while it is in flight
    // would be alarming and wrong.
    if (isLoading) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-slate-500">
          Loading layout…
        </div>
      );
    }
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p>That template no longer exists.</p>
        <Link to="/settings/documents" className="text-blue-600 hover:underline">
          Back to documents
        </Link>
      </div>
    );
  }

  if (!draft) return null;

  const readOnly = template.isStock;
  const dirty =
    JSON.stringify(draft) !== JSON.stringify(template.layout) || name !== template.name;
  const selectedBand = draft.bands.find((b) => b.id === selectedBandId) ?? null;

  const updateBand = (next: Band) =>
    setDraft({ ...draft, bands: draft.bands.map((b) => (b.id === next.id ? next : b)) });

  const handleSave = async () => {
    try {
      await saveTemplate(template.id, name.trim() || template.name, draft);
      showToast({ message: 'Layout saved', type: 'success' });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : 'Could not save the layout.',
        type: 'error',
      });
    }
  };

  const handleDuplicate = async () => {
    const id = await duplicateTemplate(template.id);
    if (!id) return;
    showToast({ message: 'Copy created — you are now editing it', type: 'success' });
    navigate(`/settings/documents/${id}`);
  };

  const bandToDelete = draft.bands.find((b) => b.id === pendingDeleteId) ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2">
        <Link
          to="/settings/documents"
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Documents
        </Link>

        <span className="mx-1 h-4 w-px bg-slate-200" />

        <FileText className="h-4 w-4 text-slate-400" />
        <input
          value={name}
          readOnly={readOnly}
          onChange={(e) => setName(e.target.value)}
          aria-label="Template name"
          className={cn(
            'h-8 min-w-48 rounded border border-transparent px-1.5 text-sm font-medium text-slate-800',
            !readOnly && 'hover:border-slate-200 focus:border-sky-400 focus:outline-none',
          )}
        />
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
          {DOC_TYPE_LABEL[template.docType]}
        </span>
        {template.isDefault && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200">
            <Star className="h-3 w-3" />
            Default
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {readOnly ? (
            <Button size="sm" leftIcon={Copy} disabled={isSaving} onClick={() => void handleDuplicate()}>
              Duplicate to edit
            </Button>
          ) : (
            <>
              {!template.isDefault && (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={Star}
                  disabled={isSaving}
                  onClick={() => {
                    void setDefault(template.id);
                    showToast({
                      message: `${template.name} is now the default ${DOC_TYPE_LABEL[
                        template.docType
                      ].toLowerCase()} layout`,
                      type: 'success',
                    });
                  }}
                >
                  Set as default
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                leftIcon={Undo2}
                onClick={() => {
                  // Discard leaves the builder rather than resetting it in
                  // place. Reverting the draft locally could not be trusted to
                  // show the saved document again: the preview only rebuilds
                  // when its digest moves, so a revert landing on the same
                  // bytes left the previous PDF on screen. Unmounting the
                  // builder disposes of that render entirely, and re-entering
                  // rebuilds from the server copy. Enabled regardless of
                  // `dirty` — leaving is always a valid thing to ask for.
                  void refresh();
                  navigate('/settings/documents');
                }}
              >
                Discard
              </Button>
              <Button size="sm" disabled={!dirty || isSaving} onClick={() => void handleSave()}>
                {isSaving ? 'Saving…' : 'Save layout'}
              </Button>
            </>
          )}
        </div>
      </div>

      {readOnly && (
        <div className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
          This is the layout Stratum ships. Duplicate it to make your own version — the original
          stays available as a fallback.
        </div>
      )}

      {/* ── Body: bands · inspector · preview ───────────────────────────── */}
      <div className="flex min-h-0 flex-1">
        {/* Band list */}
        <div
          className={cn(
            'flex w-56 shrink-0 flex-col overflow-auto border-r border-slate-200 p-2',
            readOnly && 'pointer-events-none opacity-60',
          )}
        >
          <button
            type="button"
            onClick={() => setSelectedBandId(null)}
            className={cn(
              'mb-2 rounded border px-2 py-1.5 text-left text-xs',
              selectedBandId === null
                ? 'border-sky-300 bg-sky-50 text-sky-800'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            Page &amp; style
          </button>

          <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Bands
          </p>

          <div className="space-y-1">
            {draft.bands.map((band, i) => (
              <div
                key={band.id}
                className={cn(
                  'group flex items-center gap-1 rounded border px-1.5 py-1',
                  selectedBandId === band.id
                    ? 'border-sky-300 bg-sky-50'
                    : 'border-slate-200 hover:bg-slate-50',
                  !band.enabled && 'opacity-50',
                )}
              >
                <input
                  type="checkbox"
                  checked={band.enabled}
                  aria-label={`Print ${BAND_KIND_LABEL[band.kind]}`}
                  onChange={(e) => updateBand({ ...band, enabled: e.target.checked })}
                  className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                <button
                  type="button"
                  onClick={() => setSelectedBandId(band.id)}
                  className="min-w-0 flex-1 truncate text-left text-xs text-slate-700"
                  title={BAND_KIND_LABEL[band.kind]}
                >
                  {BAND_KIND_LABEL[band.kind]}
                </button>
                {DELETABLE_KINDS.includes(band.kind) && (
                  <button
                    type="button"
                    aria-label="Remove band"
                    onClick={() => setPendingDeleteId(band.id)}
                    className="shrink-0 text-slate-300 hover:text-red-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
                {/* Reordering is offered only while the band is switched off —
                    see the note under the list. The placeholder keeps the rows
                    the same width so the list does not shift as bands are
                    ticked. */}
                {band.enabled ? (
                  <span className="w-3 shrink-0" aria-hidden="true" />
                ) : (
                  <div className="flex shrink-0 flex-col">
                    <button
                      type="button"
                      aria-label="Move band up"
                      disabled={i === 0}
                      onClick={() => setDraft({ ...draft, bands: moveItem(draft.bands, i, -1) })}
                      className="text-[8px] leading-none text-slate-400 enabled:hover:text-slate-700 disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      aria-label="Move band down"
                      disabled={i === draft.bands.length - 1}
                      onClick={() => setDraft({ ...draft, bands: moveItem(draft.bands, i, 1) })}
                      className="text-[8px] leading-none text-slate-400 enabled:hover:text-slate-700 disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => {
              const next = appendBand(draft, 'text');
              setDraft(next);
              // Select what was just added — the operator's next action is
              // always to type into it.
              setSelectedBandId(next.bands[next.bands.length - 2]?.id ?? null);
            }}
            className="mt-2 flex items-center justify-center gap-1 rounded border border-dashed border-slate-300 py-1.5 text-xs text-slate-500 hover:border-sky-300 hover:text-sky-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Add text block
          </button>

          <p className="mt-2 px-1 text-[10px] leading-snug text-slate-400">
            Untick a band to leave it out of the printed document. Only text blocks can be removed.
          </p>
          <p className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] leading-snug text-amber-800">
            To move a band, untick it first, move it, then tick it again.
          </p>
        </div>

        {/* Inspector */}
        <div
          className={cn(
            'w-80 shrink-0 overflow-auto border-r border-slate-200 p-3',
            readOnly && 'pointer-events-none opacity-60',
          )}
        >
          <p className="mb-2 text-xs font-semibold text-slate-700">
            {selectedBand ? BAND_KIND_LABEL[selectedBand.kind] : 'Page & style'}
          </p>
          {selectedBand ? (
            <BandInspector
              band={selectedBand}
              page={draft.page}
              inHeaderGrid={isInHeaderGrid(draft, selectedBand.id)}
              logoStatus={logoStatus}
              logoError={logoError}
              organisation={previewData.organisation}
              bank={bank}
              referenceLabels={referenceLabels}
              onChange={updateBand}
            />
          ) : (
            <PageStyleInspector
              layout={draft}
              onChange={(next) => setDraft({ ...draft, ...next })}
            />
          )}
        </div>

        {/* Preview */}
        <PdfPreviewPane template={{ ...template, layout: draft }} data={previewData} />
      </div>

      <ConfirmDialog
        open={bandToDelete !== null}
        title="Remove this text block?"
        message="The block and its content are removed from the layout. This is not saved until you save the layout."
        confirmLabel="Remove"
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={() => {
          setDraft({ ...draft, bands: draft.bands.filter((b) => b.id !== pendingDeleteId) });
          if (selectedBandId === pendingDeleteId) setSelectedBandId(null);
          setPendingDeleteId(null);
        }}
      />
    </div>
  );
}
